import type { HokmMove, HokmView } from "@bing/engine-hokm";
import type { BotDifficulty, CardDeckId, HokmMatchLength } from "@bing/shared";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MatchEndCard } from "../components/MatchEndCard";
import { Reveal } from "../components/Reveal";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import { botLabel, HokmTable } from "../screens/HokmTable";
import { faDigits } from "../i18n/digits";
import { presetAvatarFor } from "./seat-avatar";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "./seat-identity";
import {
  getBotDifficulty,
  getCardDeck,
  getHokmMatchLength,
  getHokmModeKot,
  getHokmModes,
  setBotDifficulty,
  setCardDeck,
  setHokmMatchLength,
  setHokmModeKot,
  setHokmModes,
} from "../settings/prefs";
import { takeResumeIntent } from "./fresh-solo";
import { HokmMatchLengthSelect } from "../components/HokmMatchLengthSelect";
import { SoloStartScreen } from "../components/SoloStartScreen";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";
import { CardDeckContext } from "./card-decks";
import { useDeckGate } from "./use-card-faces";
import { handKeyOf, useHandDeck } from "./hand-deck";

/** The match's end card for Hokm's TEAM scoring — card-98 item 12's shared template
 * (`MatchEndCard`): the winning feel, a row a team — its two players' faces and NAMES, as شلم draws a
 * team (the felt's own resolvers: a real player by name, a bot by the table's label) —, Hokm's own
 * ①② and its match points (highest wins), and play-again / exit. `result.winners` are seats, so the
 * winning team is the team of those seats. Exported so the online Hokm game reuses the exact same
 * end-of-match overlay. */
export function WinOverlay({
  result,
  teamScores,
  you,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  result: GameResult;
  teamScores: readonly number[];
  you: number;
  /** The signed-in user's identity — their own row wears their real photo + name. */
  self?: SeatIdentity | null;
  /** The lobby's known seat occupants (online); solo → undefined, the seats read the table's labels. */
  players?: readonly SeatPlayer[];
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const yourTeam = you % 2;
  const won = result.winners.some((seat) => seat % 2 === yourTeam);
  const seats = teamScores.length * 2;
  const ranked = teamScores
    .map((score, team) => ({ team, score }))
    .sort((a, b) => b.score - a.score);
  const ranks = ["①", "②"];
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.hokm.you,
      // the felt's own label (HokmTable `botLabel`): the card and the felt call a chair the same
      fallback: (st) => botLabel({ you, seats }, st),
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, you, self, players, presetAvatarFor);
  return (
    <MatchEndCard
      label={fa.hokm.gameOver}
      outcome={won ? "won" : "lost"}
      glyph="🎴"
      title={won ? fa.hokm.youWon : fa.hokm.youLost}
      reason={fa.hokm.scores}
      sides={ranked.map((r, i) => ({
        key: `team-${r.team}`,
        mine: r.team === yourTeam,
        mark: <span style={{ color: "var(--accent)", fontWeight: 800 }}>{ranks[i]}</span>,
        members: [r.team, r.team + 2]
          .filter((s) => s < seats)
          .map((s) => ({ seat: s, name: nameOf(s), avatar: avatarOf(s) })),
        result: faDigits(r.score),
        attrs: { "data-team": r.team },
      }))}
      promo
      playAgain={fa.hokm.playAgain}
      leave={fa.hokm.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** Owns the Hokm connection + store for ONE match at a FIXED (locked) match length. */
function HokmSoloRound({
  difficulty,
  matchLength,
  modeKot,
  hokmModes,
  onAgain,
}: {
  /** FIXED for this match — chosen on the start screen, never changes mid-game. */
  difficulty: BotDifficulty;
  /** Locked for the whole match — «بازی دوباره» returns to the picker, exactly like منچ. */
  matchLength: HokmMatchLength;
  /** Locked for the whole match, exactly like the length. */
  modeKot: boolean;
  /** May the Hakem call سرس / نرس / آس نرس? Locked for the whole match, like the two above. */
  hokmModes: boolean;
  onAgain: () => void;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<HokmMove, HokmView>({
          wsUrl: GAME_SERVER_URL,
          room: "hokm",
          getInitData: () => initData,
          // FIXED for this match — chosen on the start screen, never changes mid-game.
          difficulty,
          matchLength,
          modeKot,
          hokmModes,
        }),
      ),
    // `round` bumps to start a fresh game (new connection) on "play again".
    [initData, round, difficulty, matchLength, modeKot, hokmModes],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // Hold the summary overlay for a beat so the final move/trick animates first.
  const showEnd = useEndReveal(result !== null);
  // The win/lose chime lands WITH the summary (it used to fire ~1.4s early).
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it (that leaked English).
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  // Real-progress loading bar that GATES the game start: the bar fills as the room
  // connects + the first view lands, and only then signals the server to start the bots.
  // The classic SVG faces are a separate chunk (see `game/card-faces.ts`): the table may not
  // be revealed until they are in, or it would deal blank cards. The bar's floor is a MINIMUM,
  // not a timeout — on a slow connection it simply waits longer.
  const deck = useContext(CardDeckContext);
  const faces = useDeckGate(deck);
  const { loading, progress, waitingForAssets } = useSoloLoad(
    view != null,
    ready,
    store,
    faces.ready,
  );
  // the deck this table DRAWS: latched per hand from the felt's first card — no hand while the
  // loading screen is up (`hand-deck.ts`, N1) — a pick lands at the next deal, never mid-hand
  const drawDeck = useHandDeck(deck, loading ? null : handKeyOf("hokm", view));

  // «خروج» — leave the screen but KEEP the game resumable (a plain disconnect no
  // longer ends it server-side; the ActiveGame row stays, so it shows in the lobby's
  // in-progress strip).
  const doLeave = (): void => {
    store.getState().disconnect();
    setView("lobby");
  };
  // «پایان بازی» — END the game server-side and WAIT for the server's ack (the
  // ActiveGame row is provably deleted) BEFORE dropping it locally and leaving. On
  // failure/timeout stay in the game, keep it listed as in-progress (it IS), and
  // surface a retry dialog — never navigate-and-forget an unconfirmed end.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("hokm"); // off the resume strip right away; Lobby refetch confirms
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  // The in-game exit is gated behind the two-option confirm dialog first.
  const leave = (): void => setConfirmingLeave(true);
  const again = (): void => {
    store.getState().disconnect();
    // Back to the picker rather than straight into another match, so the length can be changed
    // between matches — the same shape منچ uses for its colour/seat choices.
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.hokm.connectionError}
          </p>
          <Button variant="primary" onClick={() => setView("lobby")}>
            {fa.hokm.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    // Retries are spent and the artwork still is not here: an explicit, recoverable screen —
    // never a silent stall, and never a table dealt with blank cards.
    if (faces.failed) {
      return <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />;
    }
    return (
      <Boot
        caption={waitingForAssets ? fa.status.preparingCards : fa.hokm.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.hokm.leave}
      />
    );
  }

  return (
    <CardDeckContext.Provider value={drawDeck}>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <HokmTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
        self={profile}
      />
      {result && showEnd ? (
        // The game is already over (its ActiveGame was cleared by the terminal
        // finish), so its exit is a plain return to the lobby — no keep/end choice.
        <WinOverlay
          result={result}
          teamScores={view.teamScores}
          you={view.you}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        // The server never ACKed the end — the game is still in progress and still
        // on the resume list. Same shared dialog, as the error surface: retry or stay.
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </CardDeckContext.Provider>
  );
}

/** A start-screen switch row. Two settings share this shape, so it is declared once — a second
 *  hand-rolled copy is how two rows that must read as one control drift apart. */
function StartSwitch({
  label,
  checked,
  onChange,
  on,
  off,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  on: string;
  off: string;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "5px 10px",
        borderRadius: 12,
        border: "1px solid rgb(var(--c-outline-variant))",
        background: checked
          ? "rgb(var(--c-secondary-container))"
          : "rgb(var(--c-surface-container-low))",
        color: "inherit",
        font: "inherit",
        cursor: "pointer",
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.35, textAlign: "start" }}>
        {label}
      </span>
      <span style={{ fontSize: 12, fontWeight: 800, flex: "none" }}>{checked ? on : off}</span>
    </button>
  );
}

/**
 * حکم start screen — the MATCH LENGTH (3/5/7 rounds) is chosen once, before the deal.
 *
 * Server-authoritative: this only proposes a value; `solo-room` validates it against the allowed
 * set and ignores it entirely on a RESUME, where the persisted state already carries the target the
 * match began with. So an old saved match always finishes at the length it started with, whatever
 * is selected here today.
 *
 * The shell — felt, header, centred card, «شروع بازی», and the opponent-level + card-design rows
 * every solo card game now asks for — is {@link SoloStartScreen}. Only حکم's OWN rules live here.
 */
function HokmStartScreen({
  matchLength,
  onMatchLengthChange,
  modeKot,
  onModeKotChange,
  hokmModes,
  onHokmModesChange,
  difficulty,
  onDifficultyChange,
  deck,
  onDeckChange,
  onStart,
  onLeave,
}: {
  matchLength: HokmMatchLength;
  onMatchLengthChange: (value: HokmMatchLength) => void;
  modeKot: boolean;
  onModeKotChange: (value: boolean) => void;
  hokmModes: boolean;
  onHokmModesChange: (value: boolean) => void;
  difficulty: BotDifficulty;
  onDifficultyChange: (value: BotDifficulty) => void;
  deck: CardDeckId;
  onDeckChange: (value: CardDeckId) => void;
  onStart: () => void;
  onLeave: () => void;
}): ReactNode {
  return (
    <SoloStartScreen
      title={fa.hokm.title}
      emoji="🃏"
      heading={fa.hokm.chooseMatchLength}
      leaveLabel={fa.hokm.leave}
      difficulty={difficulty}
      onDifficultyChange={onDifficultyChange}
      deck={deck}
      onDeckChange={onDeckChange}
      onStart={onStart}
      onLeave={onLeave}
    >
      <HokmMatchLengthSelect value={matchLength} onChange={onMatchLengthChange} compact />
      <div style={{ fontSize: 12, lineHeight: 1.35, color: "var(--hint)" }}>
        {fa.hokm.matchLengthHint}
      </div>
      {/* MAY THE HAKEM CALL سرس / نرس / آس نرس AT ALL — the same choice a private table's host
          gets, and the difference between the two public rooms. OFF by default: the standard
          game. Server-authoritative and locked for the whole match, like the length. */}
      {/* ⚠️ THE TWO SWITCHES SHARE ONE SLOT — see the same pairing on «تنظیمات میز». The card
          is a `gap: 20` column, and a gap applies to a collapsed item too, so the reveal is
          not a sibling of that gap: the pair is ONE child with no gap of its own and the 20px
          between them lives INSIDE the reveal, collapsing with it.

          ⚠️ AND THIS SCREEN IS THE WORSE OF THE TWO, which is why the reveal is not optional
          here: the card is vertically CENTRED, so an un-animated reveal moved the switch the
          finger had just left by 31px (measured at 390×780; 19px up and 63px down at 320×568),
          with ZERO animation events on the whole document. The rows transition keeps that
          movement a drift the eye can follow instead of a snap. */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <StartSwitch
          label={fa.hokm.modes}
          checked={hokmModes}
          onChange={onHokmModesChange}
          on={fa.hokm.modesSwitchOn}
          off={fa.hokm.modesSwitchOff}
        />
        {/* کوت under a suit-less call — asked ONLY when those calls are on offer, because at a
            table where سرس cannot be called it is a setting with nothing to apply to. The
            sources treat it as a pre-game agreement rather than a rule. A خال call is
            unaffected either way. */}
        <Reveal open={hokmModes}>
          <div style={{ paddingTop: 20 }}>
            <StartSwitch
              label={fa.hokm.modeKot}
              checked={modeKot}
              onChange={onModeKotChange}
              on={fa.hokm.modeKotOn}
              off={fa.hokm.modeKotOff}
            />
          </div>
        </Reveal>
      </div>
    </SoloStartScreen>
  );
}

/** Owns the picker → match flow. A RESUME skips the picker entirely (the saved match already has
 *  its target), which is what `takeResumeIntent` is for. */
export function HokmGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [matchLength, setMatchLength] = useState<HokmMatchLength>(getHokmMatchLength());
  const [modeKot, setModeKot] = useState<boolean>(getHokmModeKot());
  const [hokmModes, chooseHokmModes] = useState<boolean>(getHokmModes());
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBotDifficulty());
  const [deck, setDeck] = useState<CardDeckId>(getCardDeck());

  if (phase === "select") {
    return (
      <HokmStartScreen
        matchLength={matchLength}
        onMatchLengthChange={(v) => {
          setMatchLength(v);
          setHokmMatchLength(v);
        }}
        modeKot={modeKot}
        onModeKotChange={(v) => {
          setModeKot(v);
          setHokmModeKot(v);
        }}
        hokmModes={hokmModes}
        onHokmModesChange={(v) => {
          chooseHokmModes(v);
          setHokmModes(v);
        }}
        difficulty={difficulty}
        onDifficultyChange={(v) => {
          setDifficulty(v);
          setBotDifficulty(v);
        }}
        deck={deck}
        onDeckChange={(v) => {
          setDeck(v);
          setCardDeck(v);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  // The DECK CHOSEN ON THE START SCREEN, not the one App read when it rendered: App computes
  // `getCardDeck()` once, above this component, and never re-renders on a phase flip — so the
  // pick would not reach the table without this nested provider (the inner one wins).
  return (
    <CardDeckContext.Provider value={deck}>
      <HokmSoloRound
        matchLength={matchLength}
        modeKot={modeKot}
        hokmModes={hokmModes}
        difficulty={difficulty}
        onAgain={() => setPhase("select")}
      />
    </CardDeckContext.Provider>
  );
}
